import { useState, useEffect } from "react";
import { Box, Typography, IconButton, LinearProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";

const UploadVideo = ({
    hoverLabel = "Click or drag to upload video.",
    width = "1080px",
    height = "150px",
    backgroundColor = "#f5f5f5",
    setValue,
    setUploading,
    uploading
}) => {
    const [uploadProgress, setUploadProgress] = useState(0);
    const [filePathName, setFilePathName] = useState("");
    const [fileName, setFileName] = useState("");

    const CHUNK_SIZE = 1 * 900 * 900;
    const RequestId = useRequestIdGenerater();
    const { generateTimeStamp, rendomRequestId } = useTimeStamp();

    const uploadChunk = async (file, chunk, chunkIndex, totalChunks) => {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", chunk);
        formData.append("chunkIndex", chunkIndex);
        formData.append("totalChunks", totalChunks);
        formData.append("fileName", file.name);

        try {
            const response = await axios.post(
                "/v1/upload",
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
                        "Content-Type": "multipart/form-data",
                        requestId: RequestId(
                            Math.floor(1000 + Math.random() * 9999),
                            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
                        ),
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((chunkIndex / totalChunks) * 100);
                        setUploadProgress(percentCompleted);
                    },
                }
            );

            if (response?.data !== null) {
                if (chunkIndex === totalChunks - 1) {
                    setUploading(false);
                    setFilePathName(response?.data?.data?.filePath);
                    setFileName(file.name);
                    setValue('path', response?.data?.data?.filePath);
                }
                return true;
            }
        } catch (error) {
            console.error("Upload failed", error);
            setUploading(false);
            return false;
        }
    };

    const uploadFile = async (file) => {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        setUploading(true);
        for (let i = 0; i < totalChunks; i++) {
            const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const success = await uploadChunk(file, chunk, i, totalChunks);
            if (!success) {
                console.error("Failed to upload chunk", i);
                return;
            }
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (!file) return;

        const fileSizeMB = file.size / (1024 * 1024);
        const maxSizeMB = 200;

        if (fileSizeMB > maxSizeMB) {
            alert("File size exceeds 200MB. Please upload a smaller video.");
            return;
        } else {
            if (file.type === "video/mp4") {
                uploadFile(file);
            } else {
                alert("Please upload a valid video file (MP4).");
            }
        }

    };


    const handleDelete = () => {
        setFilePathName("");
        setFileName("");
        setUploadProgress(0);
        setUploading(false);
        setValue("path", "");
    };

    useEffect(() => {
        generateTimeStamp();
    }, []);

    return (
        <Box
            border={`2px dashed #aaa`}
            borderRadius="8px"
            padding="16px"
            textAlign="center"
            width={width}
            height={height}
            bgcolor={backgroundColor}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
        >
            {!filePathName ? (
                <>
                    <input
                        onChange={handleFileChange}
                        accept="video/*"
                        style={{ display: "none" }}
                        id="video-upload"
                        type="file"
                    />
                    <label htmlFor="video-upload" style={{ cursor: "pointer" }}>
                        <CloudUploadIcon fontSize="large" />
                        <Typography mt={1}>
                            {uploading ? `Uploading... ${uploadProgress}%` : hoverLabel}
                        </Typography>
                    </label>
                    {uploading && (
                        <Box width="100%" mt={2}>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                        </Box>
                    )}
                </>
            ) : (
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="body1">{fileName}</Typography>
                    <IconButton onClick={handleDelete} color="error">
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export default UploadVideo;
